import React, { useState } from "react";
import styled from "styled-components";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Button } from "components/atoms";
import { log, LogLevel } from "utils/LogUtil";
import Text from "components/atoms/Text";
import Icon from "components/atoms/Icon";
import Breadcrumb from "components/molecules/Breadcrumb";
import PremiumBadge from "components/molecules/PremiumBadge";
import Dropdown, { DropdownMenuItem } from "components/molecules/Dropdown";
import { UserManualManager } from "components/organisms/CommonUI";
import { COLOR_SYSTEM } from "components/design/design-system";
import { AppDispatch, RootState } from "store";
import { eventLog } from "utils/AmplitudeUtil";
import { logout } from "store/slices/userSlice";
import { useChannelIOApi } from "react-channel-plugin";

const HeaderContainer = styled.header<{ withoutPath?: boolean }>`
  width: 100%;
  min-height: ${(props) => (props.withoutPath ? "72px" : "100px")};
  display: flex;
  flex-direction: column;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
  box-sizing: border-box;
`;

const TopSection = styled.div`
  height: 72px;
  padding: 0 32px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${COLOR_SYSTEM.get("Gray")?.[50]};
`;

const BreadcrumbSection = styled.div`
  padding: 0px 32px 0px 32px;
  height: 30px;
  display: flex;
  align-items: center;
`;

const LogoSection = styled.div<{ $clickable?: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  cursor: ${props => props.$clickable ? 'pointer' : 'default'};
`;

const Logo = styled.img`
  height: 32px;
`;

const ServiceName = styled.span`
  font-size: 22px;
  font-weight: 700;
  color: ${COLOR_SYSTEM.get("Gray")?.[800]};
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  position: relative;
`;

const UserAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UserDropdownWrapper = styled.div`
  position: relative;
`;

const PointsBalance = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 8px 16px;
  background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  border-radius: 20px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[100]};
  }
`;

const CustomDropdownMenu = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: ${COLOR_SYSTEM.get("Gray")?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get("Gray")?.[100]};
  border-radius: 8px;
  box-shadow: 0 4px 12px ${COLOR_SYSTEM.get("Gray")?.[900]}24;
  min-width: 180px;
  padding: 8px 0;
  z-index: 1000;
`;

const DropdownItemButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border: none;
  background: none;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: ${COLOR_SYSTEM.get("Gray")?.[50]};
  }
`;

interface CommonHeaderProps {
  rightContent?: React.ReactNode;
  withoutPath?: boolean;
  logoNavigate?: boolean;
}

const CommonHeader: React.FC<CommonHeaderProps> = ({
  rightContent,
  withoutPath = false,
  logoNavigate = false,
}) => {
  const navigate = useNavigate();
  const { isAuthenticated, user, isPremiumUser } = useSelector((state: RootState) => state.user);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const { openWorkflow } = useChannelIOApi();

  const handleAdminClick = () => {
    // 편집 모드 체크
    const isInEditMode = (window as any).__isInEditMode;
    if (isInEditMode) {
      const confirmResult = window.confirm(
        "편집 중인 내용이 있습니다.\n\n정말 이 페이지를 벗어나시겠습니까?"
      );
      
      if (!confirmResult) {
        log(LogLevel.INFO, "[CommonHeader] 사용자가 네비게이션 취소");
        return;
      }
      
      // 사용자가 확인했으면 상태 초기화
      (window as any).__isInEditMode = false;
    }
    
    navigate("/admin");
  };

  const handleUserSectionClick = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen);
  };

  const handleLogout = async () => {
    eventLog("로그아웃");
    await dispatch(logout()).unwrap();
    navigate("/login");
    setIsUserDropdownOpen(false);
  };

  const handleContactSupport = () => {
    // TODO: 1:1상담 로직 구현
    console.log("1:1상담 클릭됨");
    setIsUserDropdownOpen(false);
    openWorkflow("761739");
  };

  const handleMyPage = () => {
    // TODO: 내 프로필 로직 구현
    console.log("내 프로필 클릭됨");
    setIsUserDropdownOpen(false);
    
    // 편집 모드 체크
    const isInEditMode = (window as any).__isInEditMode;
    if (isInEditMode) {
      const confirmResult = window.confirm(
        "편집 중인 내용이 있습니다.\n\n정말 이 페이지를 벗어나시겠습니까?"
      );
      
      if (!confirmResult) {
        log(LogLevel.INFO, "[CommonHeader] 사용자가 네비게이션 취소");
        return;
      }
      
      // 사용자가 확인했으면 상태 초기화
      (window as any).__isInEditMode = false;
    }
    
    navigate("/mypage");
  };

  const handleUserGuideClick = () => {
    eventLog("사용법 보기");
    // UserManualManager API로 팝업 열기
    UserManualManager.show({
      // baseVersion 없음 → 첫 페이지부터
      shouldUpdateVersion: false,  // 수동 팝업: DB 업데이트 안 함
    });
  };

  // 드롭다운 외부 클릭 시 닫기
  const handleBlur = () => {
    setTimeout(() => setIsUserDropdownOpen(false), 150);
  };

  // 포인트 잔액 클릭 핸들러
  const handlePointsClick = () => {
    eventLog("포인트_잔액_클릭");
    
    // 편집 모드 체크
    const isInEditMode = (window as any).__isInEditMode;
    if (isInEditMode) {
      const confirmResult = window.confirm(
        "편집 중인 내용이 있습니다.\n\n정말 이 페이지를 벗어나시겠습니까?"
      );
      
      if (!confirmResult) {
        log(LogLevel.INFO, "[CommonHeader] 사용자가 네비게이션 취소");
        return;
      }
      
      // 사용자가 확인했으면 상태 초기화
      (window as any).__isInEditMode = false;
    }
    
    navigate("/mypage/billing");
  };

  return (
    <HeaderContainer withoutPath={withoutPath}>
      <TopSection>
        <LogoSection
          $clickable={logoNavigate}
          onClick={() => {
            if (logoNavigate) {
              // 편집 모드 체크
              const isInEditMode = (window as any).__isInEditMode;
              if (isInEditMode) {
                const confirmResult = window.confirm(
                  "편집 중인 내용이 있습니다.\n\n정말 이 페이지를 벗어나시겠습니까?"
                );
                
                if (!confirmResult) {
                  log(LogLevel.INFO, "[CommonHeader] 사용자가 네비게이션 취소");
                  return;
                }
                
                // 사용자가 확인했으면 상태 초기화
                (window as any).__isInEditMode = false;
              }
              
              navigate("/");
            }
          }}
        >
          <Logo src="/images/logo.png" alt="약문약답 로고" />
          <ServiceName>PhAI</ServiceName>
        </LogoSection>
        <RightSection>
          {isAuthenticated && user && (
            <>
              {isPremiumUser && <PremiumBadge size="small" />}
              <PointsBalance onClick={handlePointsClick}>
                <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                  잔여 포인트:
                </Text>
                <Text textType="Body2SemiBold" color={COLOR_SYSTEM.get("Blue")?.[600]}>
                  {user.userPoints?.currentPoints?.toLocaleString() || "0"}P
                </Text>
              </PointsBalance>
              <Button
                type="Icon"
                size="Medium"
                variant="Ghost"
                color="Tertiary"
                icon="Question Mark"
                onClick={handleUserGuideClick}
              />
              {user.roles?.includes("admin") && (
                <Button
                  type="Icon"
                  size="Medium"
                  variant="Ghost"
                  color="Primary"
                  icon="SettingFilled"
                  onClick={handleAdminClick}
                />
              )}
              <UserDropdownWrapper onBlur={handleBlur}>
                <UserSection onClick={handleUserSectionClick}>
                  <UserAvatar>
                    <Icon name="User" size={16} color={COLOR_SYSTEM.get("Gray")?.[700]} />
                  </UserAvatar>
                  <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                    {user.name || "사용자"}
                  </Text>

                  <Icon
                    name={isUserDropdownOpen ? "Caret Up" : "Caret Down"}
                    size={16}
                    color={COLOR_SYSTEM.get("Gray")?.[500]}
                  />
                </UserSection>
                {isUserDropdownOpen && (
                  <CustomDropdownMenu>
                    <DropdownItemButton onClick={handleMyPage}>
                      <Icon name="User Edit" size={16} color={COLOR_SYSTEM.get("Gray")?.[600]} />
                      <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                        마이페이지
                      </Text>
                    </DropdownItemButton>
                    <DropdownItemButton onClick={handleContactSupport}>
                      <Icon name="Chat" size={16} color={COLOR_SYSTEM.get("Gray")?.[600]} />
                      <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                        이용문의
                      </Text>
                    </DropdownItemButton>
                    <DropdownItemButton onClick={handleLogout}>
                      <Icon
                        name="Arrow Circle Left"
                        size={16}
                        color={COLOR_SYSTEM.get("Gray")?.[600]}
                      />
                      <Text textType="Body2Medium" color={COLOR_SYSTEM.get("Gray")?.[700]}>
                        로그아웃
                      </Text>
                    </DropdownItemButton>
                  </CustomDropdownMenu>
                )}
              </UserDropdownWrapper>
            </>
          )}
          {rightContent}
        </RightSection>
      </TopSection>
      {!withoutPath && (
        <BreadcrumbSection>
          <Breadcrumb />
        </BreadcrumbSection>
      )}
    </HeaderContainer>
  );
};

export default CommonHeader;
